import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Edit, Save, X, Users, Target, History, Phone, Award, BookOpen, FileText, Lock, Rocket, Scale } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const API = process.env.REACT_APP_BACKEND_URL;

const AboutPage = () => {
  const { user, token } = useAuth();
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // which section is being edited
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.is_admin || user?.is_operator_admin || 
                  ['club_officer', 'ngo_officer'].includes(user?.member_class);

  useEffect(() => {
    fetchAbout();
  }, []);

  const fetchAbout = async () => {
    try {
      const res = await fetch(`${API}/api/club/about`);
      if (res.ok) {
        const data = await res.json();
        setAbout(data);
      }
    } catch (error) {
      console.error('Error fetching about:', error);
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (section, value) => {
    setEditing(section);
    setEditValue(value);
  };

  const cancelEditing = () => {
    setEditing(null);
    setEditValue('');
  };

  const saveSection = async (section) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/club/about`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ [section]: editValue })
      });

      if (res.ok) {
        const updated = await res.json();
        setAbout(updated);
        setEditing(null);
        toast.success('Section updated!');
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    { key: 'mission', title: 'Our Mission', icon: Target },
    { key: 'who_we_are', title: 'Who We Are', icon: Users },
    { key: 'what_we_do', title: 'What We Do', icon: Award },
    { key: 'history', title: 'Our History', icon: History },
    { key: 'leadership', title: 'Leadership', icon: Users },
    { key: 'contact_info', title: 'Contact Us', icon: Phone },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      <SEO 
        title="About Us"
        description="Learn about All American Dog Club - a member-governed community dedicated to connecting dog people and supporting animal welfare organizations."
        url="/about"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
            About All American Dog Club
          </h1>
          <p className="text-xl text-amber-100 max-w-2xl mx-auto">
            More than a community — a platform for real-world collaboration among dog enthusiasts
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Quick Links */}
        <div className="mb-8 flex flex-wrap gap-4 justify-center">
          <Link to="/about/story">
            <Button variant="outline" className="gap-2">
              <BookOpen className="w-4 h-4" />
              Our Story
            </Button>
          </Link>
          <Link to="/about/future">
            <Button variant="outline" className="gap-2">
              <Rocket className="w-4 h-4" />
              Our Vision
            </Button>
          </Link>
          <Link to="/about/bylaws">
            <Button variant="outline" className="gap-2">
              <Scale className="w-4 h-4" />
              Bylaws
            </Button>
          </Link>
          {isAdmin && (
            <Link to="/about/service-agreement">
              <Button variant="outline" className="gap-2">
                <FileText className="w-4 h-4" />
                Service Agreement
                <Lock className="w-3 h-3 text-muted-foreground" />
              </Button>
            </Link>
          )}
        </div>

        {isAdmin && (
          <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 text-sm">
              <strong>Admin Mode:</strong> Click the edit button on any section to customize the content. 
              You can use Markdown formatting for rich text.
            </p>
          </div>
        )}

        <div className="space-y-8">
          {sections.map(({ key, title, icon: Icon }) => (
            <Card key={key} className="overflow-hidden">
              <CardHeader className="bg-gray-50 border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-amber-700" />
                    </div>
                    {title}
                  </CardTitle>
                  {isAdmin && editing !== key && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => startEditing(key, about?.[key] || '')}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                {editing === key ? (
                  <div className="space-y-4">
                    <Textarea
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      rows={10}
                      className="font-mono text-sm"
                      placeholder="Enter content... (Markdown supported)"
                    />
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={cancelEditing}>
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                      <Button onClick={() => saveSection(key)} disabled={saving}>
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="prose prose-amber max-w-none">
                    <ReactMarkdown>{about?.[key] || '*No content yet*'}</ReactMarkdown>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Last Updated */}
        {about?.updated_at && (
          <p className="text-center text-sm text-muted-foreground mt-8">
            Last updated: {new Date(about.updated_at).toLocaleDateString()} 
            {about.updated_by && ` by ${about.updated_by}`}
          </p>
        )}
      </div>
    </div>
  );
};

export default AboutPage;
